import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, openPdfPreview, getFileBlob } from '../shared/apiClient';
import { getGoogleMapsUrl } from '../shared/maps';
import type { Odl, OdlAllegato, OdlReport } from '../shared/types';
import { useToast } from '../hooks/useToast';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { SecureImage } from '../components/SecureImage';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  HardHat,
  Paperclip,
  Eye,
  Download,
  Play,
  FileCheck,
  FileText,
} from 'lucide-react';

export function OdlDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [lightboxFoto, setLightboxFoto] = useState<string | null>(null);

  useEffect(() => {
    if (!lightboxFoto) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxFoto]);

  const { data: odlRes, isLoading } = useQuery<{ ok: boolean; data: Odl }>({
    queryKey: ['odl-detail', id],
    queryFn: async () => {
      const res = await apiGet<{ ok: boolean; data: Odl }>(`/odl/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const presaInCaricoMutation = useMutation({
    mutationFn: () => apiPost(`/odl/${id}/presa-in-carico`, {}),
    onSuccess: () => {
      toast({ type: 'success', message: 'ODL preso in carico!' });
      queryClient.invalidateQueries({ queryKey: ['odl-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['odl-squadra-list'] });
    },
    onError: (err: any) => {
      toast({ type: 'error', message: err.response?.data?.errore || err.message || 'Errore presa in carico' });
    },
  });

  const odl = odlRes?.data;

  if (isLoading) {
    return (
      <div className="text-center py-16">
        <div className="w-8 h-8 border-3 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-xs text-slate-500 mt-2">Caricamento cantiere...</p>
      </div>
    );
  }

  if (!odl) {
    return (
      <div className="card-section text-center py-12">
        <p className="text-sm font-semibold text-slate-700">Ordine Di Lavoro non trovato</p>
        <button
          type="button"
          onClick={() => navigate('/odl')}
          className="mt-4 px-4 py-2 bg-sky-600 text-white text-xs font-bold rounded-lg"
        >
          Torna all'elenco ODL
        </button>
      </div>
    );
  }

  const apriAllegato = async (all: OdlAllegato) => {
    try {
      const isImg = all.tipo?.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp)$/i.test(all.nomeFile);
      if (isImg && all.pathFile) {
        const { url } = await getFileBlob(`/odl-allegati/${all.pathFile}`);
        setLightboxFoto(url);
      } else {
        await openPdfPreview(`/odl/${odl.id}/allegati/${all.id}`);
      }
    } catch (err) {
      console.error('[ODL] Errore apertura allegato:', err);
      toast({ type: 'error', message: 'Impossibile aprire il documento' });
    }
  };

  const parseMateriali = (raw: string) => {
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };

  const parseFoto = (raw: string) => {
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };

  const isPreso = odl.stato === 'preso_in_carico';
  const isDaTerminare = odl.stato === 'da_terminare';
  const isConcluso = odl.stato === 'concluso';

  return (
    <div className="space-y-4 pb-24">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/odl')}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4" /> Torna a ODL
        </button>

        <button
          type="button"
          onClick={() => openPdfPreview(`/api/odl/${odl.id}/pdf/sommario`)}
          className="flex items-center gap-1 text-xs font-bold text-sky-700 bg-sky-50 px-2.5 py-1.5 rounded-lg border border-sky-200"
        >
          <Download className="w-3.5 h-3.5" /> Sommario PDF
        </button>
      </div>

      {/* Card Principale Impianto e Ordine */}
      <div className="card-section space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-sky-100 text-sky-800">
                ORDINE COMMITTENTE: {odl.numeroOrdineCommittente}
              </span>
              {odl.priorita === 'urgente' && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                  URGENTE
                </span>
              )}
            </div>
            <h2 className="text-base font-bold text-slate-900 mt-1">
              <span className="text-emerald-700">[{odl.impianto.codice}]</span> {odl.impianto.nome}
            </h2>
          </div>

          {getGoogleMapsUrl(odl.impianto) && (
            <a
              href={getGoogleMapsUrl(odl.impianto)!}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1.5 rounded-lg border border-emerald-200 shrink-0"
            >
              <MapPin className="w-3.5 h-3.5" /> Mappe
            </a>
          )}
        </div>

        {/* Descrizione Lavorazione */}
        <div className="pt-2 border-t border-slate-100 space-y-1">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            Lavorazione Programmata
          </div>
          <p className="text-xs text-slate-800 whitespace-pre-wrap leading-relaxed">
            {odl.descrizioneLavoro}
          </p>
        </div>

        {/* Metadati */}
        <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" /> Apertura: {new Date(odl.dataApertura).toLocaleDateString('it-IT')}
          </span>
          {odl.dataScadenza && (
            <span className="flex items-center gap-1 text-amber-700 font-semibold">
              Termine: {new Date(odl.dataScadenza).toLocaleDateString('it-IT')}
            </span>
          )}
          <span className="flex items-center gap-1">
            <HardHat className="w-3.5 h-3.5" /> {odl.squadra ? odl.squadra.nome : 'Non assegnata'}
          </span>
        </div>

        {/* Blocco Azione Operativa Principale Inline */}
        <div className="pt-3 border-t border-slate-100 space-y-2">
          {(!isPreso && !isConcluso) && (
            <div className="space-y-2">
              <button
                type="button"
                disabled={presaInCaricoMutation.isPending}
                onClick={() => presaInCaricoMutation.mutate()}
                className="w-full py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-extrabold text-sm rounded-xl shadow-md shadow-emerald-600/30 flex items-center justify-center gap-2 transition"
              >
                <Play className="w-4 h-4 fill-white" />
                <span>{isDaTerminare ? 'Riprendi in carico cantiere' : 'Prendi in carico / Inizia cantiere'}</span>
              </button>
              <p className="text-[11px] text-center text-slate-500 font-medium">
                Tocca "Prendi in carico" all'arrivo per registrare l'avvio e sbloccare la compilazione del report.
              </p>
            </div>
          )}

          {isPreso && (
            <button
              type="button"
              onClick={() => navigate(`/odl/${odl.id}/report`)}
              className="w-full py-3.5 px-4 bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white font-extrabold text-sm rounded-xl shadow-md shadow-sky-600/30 flex items-center justify-center gap-2 transition animate-pulse"
            >
              <FileCheck className="w-5 h-5" />
              <span>Compila Report di Giornata</span>
            </button>
          )}

          {isConcluso && (
            <div className="py-2.5 px-4 bg-emerald-50 border border-emerald-200 text-emerald-800 font-bold text-xs rounded-xl text-center">
              ✓ Cantiere ODL Concluso Definitivamente
            </div>
          )}
        </div>
      </div>

      {/* Sezione Collassabile: Documenti & Planimetrie Fascicolo */}
      <CollapsibleSection
        icon={Paperclip}
        iconClass="bg-sky-100 text-sky-700"
        title="Fascicolo Tecnico & Planimetrie"
        subtitle={odl.allegati && odl.allegati.length > 0 ? `${odl.allegati.length} documenti disponibili` : 'Nessun allegato'}
        defaultOpen={false}
      >
        {(!odl.allegati || odl.allegati.length === 0) ? (
          <p className="text-xs text-slate-500 italic py-2">
            Nessun documento o planimetria caricato per questo cantiere.
          </p>
        ) : (
          <div className="space-y-2 pt-2">
            {odl.allegati.map((all: OdlAllegato) => {
              return (
                <div
                  key={all.id}
                  className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-200"
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <FileText className="w-4 h-4 text-sky-600 shrink-0" />
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => apriAllegato(all)}
                        className="text-xs font-bold text-slate-800 truncate text-left hover:text-sky-600"
                      >
                        {all.nomeFile}
                      </button>
                      <div className="text-[10px] text-slate-500">
                        {(all.size / 1024).toFixed(0)} KB • {all.caricatoDa || 'Centrale'}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => apriAllegato(all)}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-sky-700 bg-white border border-slate-300 rounded-md shadow-sm shrink-0 hover:bg-slate-50"
                  >
                    <Eye className="w-3.5 h-3.5" /> Apri
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleSection>

      {/* Sezione Collassabile: Giornate Precedenti */}
      <CollapsibleSection
        icon={HardHat}
        iconClass="bg-emerald-100 text-emerald-700"
        title="Giornate di Lavoro Precedenti"
        subtitle={odl.reports && odl.reports.length > 0 ? `${odl.reports.length} report registrati` : 'Nessuna giornata registrata'}
        defaultOpen={false}
      >
        {(!odl.reports || odl.reports.length === 0) ? (
          <p className="text-xs text-slate-500 italic py-2">
            Ancora nessun report di giornata compilato per questo ODL.
          </p>
        ) : (
          <div className="space-y-3 pt-2">
            {odl.reports.map((rep: OdlReport) => {
              const materiali = parseMateriali(rep.materiali);
              const foto = parseFoto(rep.foto);

              return (
                <div key={rep.id} className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between border-b border-slate-200 pb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-sky-100 text-sky-800">
                        Giornata #{rep.giornataNumero}
                      </span>
                      <span className="text-xs font-semibold text-slate-700">
                        {new Date(rep.dataLavoro || rep.createdAt).toLocaleDateString('it-IT')}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => openPdfPreview(`/api/odl/${odl.id}/pdf/giornata/${rep.id}`)}
                      className="text-[11px] font-bold text-sky-700 flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" /> PDF
                    </button>
                  </div>

                  <p className="text-xs text-slate-800 whitespace-pre-wrap">
                    {rep.descrizioneLavori || 'Nessuna descrizione inserita'}
                  </p>

                  {materiali.length > 0 && (
                    <div className="pt-1 flex flex-wrap gap-1.5">
                      {materiali.map((m: any, idx: number) => (
                        <span key={idx} className="text-[10px] bg-white border border-slate-300 px-1.5 py-0.5 rounded text-slate-700">
                          {m.nome}: {m.unita ? `${m.unita} ` : ''}{m.quantita || 1}
                        </span>
                      ))}
                    </div>
                  )}

                  {foto.length > 0 && (
                    <div className="pt-1 flex gap-2 overflow-x-auto pb-1">
                      {foto.map((fn: string, fIdx: number) => (
                        <SecureImage
                          key={fIdx}
                          src={`/api/uploads/${fn}`}
                          alt="Foto cantiere"
                          className="w-16 h-14 object-cover rounded border border-slate-300 cursor-pointer"
                          onImageClick={(blobUrl) => setLightboxFoto(blobUrl)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleSection>

      {/* Lightbox Immagine */}
      {lightboxFoto && typeof document !== 'undefined' && createPortal(
        <div
          onClick={() => setLightboxFoto(null)}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
        >
          <img
            src={lightboxFoto}
            alt="Ingrandimento"
            className="max-w-full max-h-full rounded-lg"
          />
        </div>,
        document.body
      )}
    </div>
  );
}
